export interface PricingFeature {
  name: string;
  tooltip?: string;
  professional: boolean | string;
  business: boolean | string;
  enterprise: boolean | string;
}

export interface PricingSection {
  title: string;
  features: PricingFeature[];
}

export const pricingSections: PricingSection[] = [
  {
    title: "General",
    features: [
      {
        name: "Storage",
        professional: "3GB",
        business: "8GB",
        enterprise: "Unlimited",
      },
      {
        name: "Up-Time reports and proof of play",
        professional: true,
        business: true,
        enterprise: true,
      },
      {
        name: "Audit logs",
        professional: true,
        business: true,
        enterprise: true,
      },
      {
        name: "Group screens, media, compositions, and users",
        professional: true,
        business: true,
        enterprise: true,
      },
      {
        name: "Multi User with roles management",
        professional: false,
        business: true,
        enterprise: true,
      },
      {
        name: "Multi-factor authentication (MFA)",
        professional: false,
        business: true,
        enterprise: true,
      },
      {
        name: "API access & zapier integration",
        professional: false,
        business: true,
        enterprise: true,
      },
      {
        name: "Pickcel GO",
        tooltip: "Manage your digital signage network anytime on the fly, with your mobile devices.",
        professional: true,
        business: true,
        enterprise: true,
      },
    ],
  },
  {
    title: "Media tools and management",
    features: [
      {
        name: "Media management",
        professional: true,
        business: true,
        enterprise: true,
      },
      {
        name: "Access to free media repositories",
        professional: true,
        business: true,
        enterprise: true,
      },
      {
        name: "In-built graphic design tool (Artboard)",
        professional: true,
        business: true,
        enterprise: true,
      },
      {
        name: "100+ pre-baked design templates",
        professional: true,
        business: true,
        enterprise: true,
      },
      {
        name: "60+ content apps",
        professional: true,
        business: true,
        enterprise: true,
      },
      {
        name: "Unlimited screen templates with multiple zones",
        professional: true,
        business: true,
        enterprise: true,
      },
      {
        name: "Composition and zone wise playlist",
        professional: true,
        business: true,
        enterprise: true,
      },
      {
        name: "Live zoom event streaming",
        tooltip: "Live stream Zoom meetings directly on your digital signage",
        professional: false,
        business: true,
        enterprise: true,
      },
      {
        name: "Supports 4K media",
        professional: false,
        business: true,
        enterprise: true,
      },
      {
        name: "Content sync",
        tooltip: "Synchronize media playback on multiple screens without a video wall controller. The feature is available only on Android devices and all the screens should have same configuration",
        professional: false,
        business: true,
        enterprise: true,
      },
      {
        name: "Microsoft power BI dashboard",
        professional: false,
        business: true,
        enterprise: true,
      },
      {
        name: "Dashboards integration",
        tooltip: "Grafana, Jira, Salesforce, Tableau and any Homegrown or Internal applications",
        professional: false,
        business: true,
        enterprise: true,
      },
      {
        name: "POS/ API integration",
        tooltip: "Square, Clover, Shopify and any other custom API",
        professional: false,
        business: true,
        enterprise: true,
      },
      {
        name: "Wallcraft",
        tooltip: "Symmetric and Asymmetric video wall",
        professional: false,
        business: true,
        enterprise: true,
      },
      {
        name: "Event Stream",
        tooltip: "Stream any content from your mobile across your digital signage screen",
        professional: false,
        business: true,
        enterprise: true,
      },
    ],
  },
  {
    title: "Screen management",
    features: [
      {
        name: "Supports Android, Windows, BrightSign, Linux, Samsung, LG, Google Chrome, iOS,firetv stick.",
        professional: true,
        business: true,
        enterprise: true,
      },
      {
        name: "Offline content support (image, video)",
        professional: true,
        business: true,
        enterprise: true,
      },
      {
        name: "Remote screen troubleshooting",
        professional: true,
        business: true,
        enterprise: true,
      },
      {
        name: "Tag based filter and grouping",
        professional: true,
        business: true,
        enterprise: true,
      },
      {
        name: "Custom screen attributes",
        tooltip: "Unique device tags based on store IDs, location, etc., allow intuitive sorting of device-specific media during automated bulk publishing",
        professional: false,
        business: false,
        enterprise: true,
      },
    ],
  },
  {
    title: "Content scheduling",
    features: [
      {
        name: "Advanced content scheduler",
        professional: true,
        business: true,
        enterprise: true,
      },
      {
        name: "Screen wise default content set",
        professional: true,
        business: true,
        enterprise: true,
      },
      {
        name: "Emergency content publish",
        professional: true,
        business: true,
        enterprise: true,
      },
      {
        name: "Campaign",
        tooltip: "Display promotional ads for a time-frame, including images or videos, apps or location-specific content using tags",
        professional: false,
        business: true,
        enterprise: true,
      },
    ],
  },
  {
    title: "Support and user experience",
    features: [
      {
        name: "Up to 99.99% Uptime",
        professional: true,
        business: true,
        enterprise: true,
      },
      {
        name: "24x7 chat support",
        professional: true,
        business: true,
        enterprise: true,
      },
      {
        name: "On call support",
        professional: false,
        business: true,
        enterprise: true,
      },
      {
        name: "Dedicated customer success manager",
        professional: false,
        business: false,
        enterprise: true,
      },
    ],
  },
  {
    title: "Advanced",
    features: [
      {
        name: "On-premise server implementation",
        professional: false,
        business: false,
        enterprise: true,
      },
      {
        name: "Single sign-on (SSO) integration",
        tooltip: "Enables secure login to Pickcel accounts through your organization’s identity provider (such as Okta, Azure AD, Auth0). Supports SAML 2.0, OIDC, and JWT-based SSO authentication.",
        professional: false,
        business: false,
        enterprise: true,
      },
      {
        name: "Custom content app development",
        professional: false,
        business: false,
        enterprise: true,
      },
      {
        name: "Custom integration with external/enterprise applications",
        professional: false,
        business: false,
        enterprise: true,
      },
      {
        name: "Interactive/ Touchscreen/ Kiosk",
        professional: false,
        business: false,
        enterprise: true,
      },
      {
        name: "Custom solutions for signage player app",
        tooltip: "Integration with external sensor and other hardware",
        professional: false,
        business: false,
        enterprise: true,
      },
      {
        name: "Remote device management with custom android launcher APK",
        professional: false,
        business: false,
        enterprise: true,
      },
      {
        name: "Unlimited storage",
        professional: false,
        business: false,
        enterprise: true,
      },
      {
        name: "Remote assistance in screen rollout",
        professional: false,
        business: false,
        enterprise: true,
      },
      {
        name: "Kiosk device support",
        professional: true,
        business: true,
        enterprise: true,
      },
    ],
  },
];

